import { FC, useContext, useEffect, useState } from 'react';
import { beautifyThePrice, isNotEmpty, replaceEmpty } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { postMessageToParent } from 'src/common/utils/ways';
import { LcnSitemapSiteViewContext, LcnSitemapSiteViewContextProps } from 'src/views/lcn/pages/sitemap/components/SiteView/index';
import { getLcnPlaceSpotList } from 'src/views/lcn/pages/sitemap/api';
import { post } from '@/common/request';
import cs from 'classnames';
import styles from './index.module.less';
import { message, Popover, Typography } from 'antd';
import IconFont from '@/common/components/IconFont';
import TagGroup from '@/common/components/TagGroup';
import AddCollectIcon from 'src/views/lcn/pages/sitemap/components/Collection/AddCollectIcon';
import V2Empty from 'src/common/components/Data/V2Empty/index';
import V2DetailItem from 'src/common/components/Feedback/V2DetailItem/index';
import V2DetailGroup from '@/common/components/Feedback/V2DetailItem/Group';

/**
 * 点位列表
 * @description
 * @demo
 */
const SpotList:FC<{
  placeId: number,
  className?: string,
  style?: any,
}> = ({ placeId, className, style = {} }) => {

  /* state */
  const priceVisible = false; // 产品说先隐藏价格
  const [requesting, setRequesting] = useState(false);

  const { fullSearchParams } = useContext<LcnSitemapSiteViewContextProps>(LcnSitemapSiteViewContext);

  const [list, setList] = useState<any[]>([]);

  /* hooks */
  useEffect(() => {
    methods.loadData();
  }, [placeId]);

  /* methods */
  const methods = useMethods({

    loadData() {
      if (!placeId) {
        setList([]);
        return;
      }
      const params = {
        ...(fullSearchParams || {}),
        placeId
      };
      getLcnPlaceSpotList(params).then((res) => {
        const data = Array.isArray(res) ? res.map((item: any) => ({
          ...item,
          tags: [item.spotCategory, item.spotFloor, item.spotSize, item.spotDayFlow].filter(item => isNotEmpty(item)).map(it => ({ label: it, color: 'grey' }))
        })) : [];
        setList(data);
      });
    },

    // 渲染价格详情
    priceDetailRender(item) {
      let result = <></>;
      const prices = item.prices;
      if (prices && Object.keys(prices).length) {
        const showPrice = (price) => isNotEmpty(price) ? `￥${beautifyThePrice(price)}` : '-';

        result = <V2DetailGroup direction='horizontal' labelLength={5} style={{ marginTop: -15 }}>
          <V2DetailItem label='工作日价格' value={showPrice(prices.workingDayPrice)}/>
          <V2DetailItem label='周五价格' value={showPrice(prices.fridayPrice)}/>
          <V2DetailItem label='周末价格' value={showPrice(prices.weekendPrice)}/>
          <V2DetailItem label='节假日价格' value={showPrice(prices.holidayPrice)}/>
          <V2DetailItem label='单周价格' value={showPrice(prices.weeklyPrice)}/>
          <V2DetailItem label='月价格' value={showPrice(prices.monthlyPrie)}/>
        </V2DetailGroup>;
      } else {
        result = <V2Empty customTip='暂无数据' />;
      }

      return <div className={styles.priceDetail} onClick={e => e.stopPropagation()}>{result}</div>;
    },

    handleClickItem(e, id) {
      e.stopPropagation();
      methods.spotDetail(id);
    },

    // 点位详情（在 LCN 的内嵌 iframe 生效）
    spotDetail(id) {
      console.log('spotDetail', id);
      id && postMessageToParent({ type: 'spotDetail', data: id }, '*');
    },

    /** 收藏/取消收藏回调 */
    collectComplete(collected) {
      console.log('collected', collected);
      methods.loadData();
    },

    // 添加到 lcn
    addLcn(e, id) {
      e.stopPropagation();

      const requirementId = fullSearchParams.requirementId;
      if (!id || !requirementId) {
        return;
      }

      const params = {
        requirementId,
        spotIds: [id], // 点位 id
      };
      setRequesting(true);
      // https://yapi.lanhanba.com/project/602/interface/api/75354
      post('/lcn/recommend/recommendSpotBatch', params, { isMock: false, mockId: 602, mockSuffix: '/api' }).then(() => {
        message.success('添加成功');
      }).finally(() => setRequesting(false));
    },

  });

  return (<div className={cs(styles.spotList, className)} style={style}>
    {Array.isArray(list) && !!list.length ? <>
      {list.map((item, index) => <div key={index} className={styles.spotListItem} onClick={(e) => methods.handleClickItem(e, item.tntSpotId)}>
        <div className={styles.titleWrapper}>
          <Typography.Text ellipsis={{ tooltip: item.name }} className={styles.title}>{replaceEmpty(item.name)}</Typography.Text>
          <div className={styles.icons}>
            <div className={cs(styles.addLcn, requesting && styles.disabled)} onClick={(e) => methods.addLcn(e, item.id)}>+LCN</div>
            <AddCollectIcon id={item.id} collectStatus={item.isFavourate} collectComplete={methods.collectComplete} />
          </div>
        </div>
        {!!priceVisible && <div className={cs(styles.priceWrapper, 'mt-6')}>
          <span className='fs-12 c-999'>工作日价格</span>
          <Popover content={methods.priceDetailRender(item)} overlayClassName={styles.pricePopover}>
            <span className={styles.price}>￥{beautifyThePrice(item.price)}</span>
          </Popover>
          <IconFont iconHref='pc-common-icon-ic_arrow_right' className={cs('mr-2 fs-10', styles.addressRow)}/>
        </div>}
        <TagGroup tags={item.tags} className={cs(styles.tags, 'mt-6')}/>
      </div>)}
    </> : null}

    {!Array.isArray(list) || list.length <= 1 ? <V2Empty customImg={`${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/map/img_spot_list_empty.png`} customTip='无更多点位，重新筛选' className={styles.spotListEmpty} /> : null}
  </div>);
};

// const prices = {
//   workingDayPrice: 35000000,
//   fridayPrice: 35000,
//   weekendPrice: 35000,
//   holidayPrice: 35000,
//   weeklyPrice: 0,
//   monthlyPrie: null,
// };

// const mockList = [
//   { id: 1, name: '一楼大中庭', isFavourate: false, price: 55000, tags: ['中庭', 'L1', '20*20m²'], prices },
//   { id: 2, name: '一楼大中庭', isFavourate: false, price: 55000, tags: ['中庭', 'L1', '20*20m²'], prices },
//   { id: 3, name: '一楼大中庭一楼大中庭一楼大中庭一楼大中庭', isFavourate: true, price: 550000000, tags: ['中庭', 'L1', '20*20m²', '20*20m²', '20*20m²', '20*20m²'], prices },
//   { id: 4, name: '一楼大中庭', isFavourate: false, price: 55000, tags: ['中庭', 'L1', '20*20m²'], prices },
//   { id: 5, name: '一楼大中庭', isFavourate: false, price: 55000, tags: ['中庭', 'L1', '20*20m²'], prices },
//   { id: 6, name: '一楼大中庭', isFavourate: false, price: 55000, tags: ['中庭', 'L1', '20*20m²'], prices },
//   { id: 7, name: '一楼大中庭', isFavourate: false, price: 55000, tags: ['中庭', 'L1', '20*20m²'], prices },
//   { id: 8, name: '一楼大中庭', isFavourate: false, price: 55000, tags: ['中庭', 'L1', '20*20m²'] },
// ];

export default SpotList;
